import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, forkJoin, from, Observable, of, switchMap, withLatestFrom } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { MeasureActions } from '../measure.actions';
import { Store } from '@ngrx/store';
import { selectCurrentSurveyVersionId, selectQuestionTypes } from '../measure.selector';
import { QuestionService } from '../../services/question.service';
import { defaultQuestionPlaceholdersConfig } from '../../config/default-question-placeholders.config';

@Injectable()
export class QuestionEffects {
  currentMeasureVersionId$ = this.store.select(selectCurrentSurveyVersionId);
  questionTypes$ = this.store.select(selectQuestionTypes);

  addQuestion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.addQuestion),
      withLatestFrom(this.currentMeasureVersionId$, this.questionTypes$),
      filter(([_, surveyVersionId, questionTypes]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId, questionTypes]) =>
        // set first type as initial one
        this.questionService
          .addQuestion(action.scaleId, defaultQuestionPlaceholdersConfig.display, questionTypes[0])
          .pipe(
            map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
            catchError(() => of(MeasureActions.addQuestionFailure())),
          ),
      ),
    ),
  );

  updateQuestionExportTag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateQuestionExportTag),
      withLatestFrom(this.currentMeasureVersionId$),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.questionService.updateQuestionExportTag(action.questionId, action.dataExportTag).pipe(
          tap((result) => console.log('Updated question:', result)),
          mergeMap(() =>
            from([
              MeasureActions.getSurveyVersionTree({ surveyVersionId }),
              MeasureActions.getAllSurveyVersionQuestions({ versionId: surveyVersionId }),
            ]),
          ),
          catchError(() => of(MeasureActions.updateQuestionExportTagFailure())),
        ),
      ),
    ),
  );

  removeQuestion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.removeQuestion),
      withLatestFrom(this.currentMeasureVersionId$),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.questionService.removeQuestion(action.questionId).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError(() => of(MeasureActions.removeQuestionFailure())),
        ),
      ),
    ),
  );

  reorderQuestion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.reorderQuestion),
      withLatestFrom(this.currentMeasureVersionId$),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.questionService.updateQuestionIndex(action.questionId, action.index).pipe(
          mergeMap(() =>
            from([
              MeasureActions.getSurveyVersionTree({ surveyVersionId }),
              MeasureActions.getAllSurveyVersionQuestions({ versionId: surveyVersionId }),
            ]),
          ),
          catchError(() => of(MeasureActions.reorderQuestionFailure())),
        ),
      ),
    ),
  );

  updateQuestionDisplay$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateQuestionDisplay),
      withLatestFrom(this.currentMeasureVersionId$),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.questionService.updateQuestionDisplay(action.questionId, action.display).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError(() => of(MeasureActions.updateQuestionDisplayFailure())),
        ),
      ),
    ),
  );

  updateQuestionType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateQuestionType),
      withLatestFrom(this.currentMeasureVersionId$),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.questionService.updateQuestionType(action.questionId, action.typeId).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError(() => of(MeasureActions.updateQuestionTypeFailure())),
        ),
      ),
    ),
  );

  updateQuestionValidationType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateQuestionValidationType),
      withLatestFrom(this.currentMeasureVersionId$),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.questionService.updateQuestionValidationType(action.questionId, action.validationType).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError(() => of(MeasureActions.updateQuestionValidationTypeFailure())),
        ),
      ),
    ),
  );

  updateQuestionGraphic$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateQuestionGraphic),
      withLatestFrom(this.currentMeasureVersionId$),
      filter(([action, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.questionService.graphicQuestion(action.questionId, action.graphic).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError(() => of(MeasureActions.updateQuestionGraphicFailure())),
        ),
      ),
    ),
  );

  updateQuestionTextEntryDefault$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateQuestionTextEntryDefault),
      withLatestFrom(this.store.select(selectCurrentSurveyVersionId)),
      filter(([_, versionId]) => versionId != null),
      switchMap(([{ questionId, textEntryDefault }, versionId]) =>
        this.questionService.updateTextEntryDefault(questionId, textEntryDefault).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId: versionId! })),
          catchError((err) => of(MeasureActions.updateQuestionTextEntryDefaultFailure({ error: err.message }))),
        ),
      ),
    ),
  );

  updateHideInOpenEndedToggle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateQuestionHideInOpenEndedToggle),
      withLatestFrom(this.store.select(selectCurrentSurveyVersionId)),
      filter(([_, versionId]) => versionId != null),
      switchMap(([{ questionId, hideInOpenEndedToggle }, versionId]) =>
        this.questionService.updateHideInOpenEndedToggle(questionId, hideInOpenEndedToggle).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId: versionId! })),
          catchError((err) => of(MeasureActions.updateQuestionHideInOpenEndedToggleFailure({ error: err.message }))),
        ),
      ),
    ),
  );

  updateQuestionUseNPSLabels$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateQuestionUseNPSLabels),
      withLatestFrom(this.store.select(selectCurrentSurveyVersionId)),
      filter(([_, versionId]) => versionId != null),
      switchMap(([{ questionId, useNPSLabels, labelIds }, versionId]) => {
        // Turning the toggle OFF also deletes the question's NPS labels so the backend
        // returns nPSLabels: null; the flag alone used to leave stale labels behind.
        const removeLabels$: Observable<unknown> =
          !useNPSLabels && labelIds.length
            ? forkJoin(labelIds.map((labelId) => this.questionService.removeNPSLabel(labelId)))
            : of(null);

        return removeLabels$.pipe(
          switchMap(() => this.questionService.updateQuestionUseNPSLabels(questionId, useNPSLabels)),
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId: versionId! })),
          catchError((err) => of(MeasureActions.updateQuestionUseNPSLabelsFailure({ error: err.message }))),
        );
      }),
    ),
  );

  updateQuestionUseReverseScaleValues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateQuestionUseReverseScaleValues),
      withLatestFrom(this.currentMeasureVersionId$),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.questionService.updateUseReverseScaleValues(action.questionId, action.useReverseScaleValues).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId: surveyVersionId as number })),
          catchError((err) =>
            of(
              MeasureActions.updateQuestionUseReverseScaleValuesFailure({
                error: err?.message ?? 'updateQuestionUseReverseScaleValues failed',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  updateQuestionUseCustomScaleValues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateQuestionUseCustomScaleValues),
      withLatestFrom(this.currentMeasureVersionId$),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.questionService.updateUseCustomScaleValues(action.questionId, action.useCustomScaleValues).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId: surveyVersionId as number })),
          catchError((err) =>
            of(
              MeasureActions.updateQuestionUseCustomScaleValuesFailure({
                error: err?.message ?? 'updateQuestionUseCustomScaleValues failed',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  updateResponseRequirements$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateResponseRequirements),
      withLatestFrom(this.currentMeasureVersionId$),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.questionService.updateResponseRequirements(action.questionId, action.value).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError(() => of(MeasureActions.updateResponseRequirementsFailure())),
        ),
      ),
    ),
  );

  removeNpsLabel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.removeNpsLabel),
      withLatestFrom(this.currentMeasureVersionId$),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.questionService.removeNPSLabel(action.labelId).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError((error) => of(MeasureActions.removeNpsLabelFailure({ error: error.message }))),
        ),
      ),
    ),
  );

  updateNpsLabelDisplay$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateNpsLabelDisplay),
      withLatestFrom(this.currentMeasureVersionId$),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.questionService.updateNpsLabelDisplay(action.labelId, action.display).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError((error) => of(MeasureActions.updateNpsLabelDisplayFailure({ error: error.message }))),
        ),
      ),
    ),
  );

  constructor(
    private store: Store,
    private actions$: Actions,
    private questionService: QuestionService,
  ) {}
}
